import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  makeStyles,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Requirement } from './types';

interface ApproveConfirmDialogProps {
  open: boolean;
  requirement: Requirement | null;
  appliedFixLabels: string[];
  onConfirm: () => void;
  onCancel: () => void;
}

const useStyles = makeStyles(theme => ({
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  label: {
    fontWeight: 600,
    minWidth: 130,
    color: theme.palette.text.secondary,
  },
  value: {
    flex: 1,
  },
  dialogContent: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  dialogActions: {
    padding: theme.spacing(2),
  },
  message: {
    marginBottom: theme.spacing(3),
  },
}));

const ApproveConfirmDialog: React.FC<ApproveConfirmDialogProps> = ({
  open,
  requirement,
  appliedFixLabels,
  onConfirm,
  onCancel,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('requirement-doctor');

  if (!requirement) return null;

  const score = requirement.analysis;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth onClose={onCancel}>
      <DialogTitle>{t('approveConfirm.title')}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="body2" className={classes.message}>
          {t('approveConfirm.message')}
        </Typography>

        <Box>
          <Box className={classes.row}>
            <Typography variant="body2" className={classes.label}>
              {t('approveConfirm.labels.requirementId')}
            </Typography>
            <Typography variant="body2" className={classes.value}>
              {requirement.id}
            </Typography>
          </Box>

          <Box className={classes.row}>
            <Typography variant="body2" className={classes.label}>
              {t('approveConfirm.labels.version')}
            </Typography>
            <Typography variant="body2" className={classes.value}>
              {requirement.versionId ?? '—'}
            </Typography>
          </Box>

          {score && (
            <>
              <Box className={classes.row}>
                <Typography variant="body2" className={classes.label}>
                  {t('approveConfirm.labels.aspiceScore')}
                </Typography>
                <Typography variant="body2" className={classes.value}>
                  {score.aspiceScore.toFixed(1)} / 10
                </Typography>
              </Box>

              <Box className={classes.row}>
                <Typography variant="body2" className={classes.label}>
                  {t('approveConfirm.labels.incoseScore')}
                </Typography>
                <Typography variant="body2" className={classes.value}>
                  {score.incoseScore.toFixed(1)} / 10
                </Typography>
              </Box>
            </>
          )}

          <Box className={classes.row}>
            <Typography variant="body2" className={classes.label}>
              {t('approveConfirm.labels.appliedFixes')}
            </Typography>
            <Typography variant="body2" className={classes.value}>
              {appliedFixLabels.length > 0
                ? appliedFixLabels.join(', ')
                : t('approveConfirm.noFixes')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onCancel} variant="outlined">
          {t('approveConfirm.buttons.cancel')}
        </Button>
        <Button onClick={onConfirm} variant="contained" color="primary">
          {t('approveConfirm.buttons.approve')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveConfirmDialog;
